import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  alpha,
  Tooltip,
  Fade,
  Rating,
  Grid,
} from "@mui/material";
import { SearchOutlined, Close } from "@mui/icons-material";
import PaginationControl from "components/shared/modal/PaginationControl";
import { format } from "utils";
import { ReviewDetails } from "./types";
import ReviewModal from "./components/Modal";
import { useAppDispatch, useAppSelector } from "store";
import { fetchReviews, invalidateReviewCache } from "slice/reviewSlice";

const MAX_ROWS_PER_PAGE = 10;

const TripReviewsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const merchant = useAppSelector((state) => state.appLayout.userDetails) as any;

  // Redux selectors
  const reviews = useAppSelector((state) => state.reviews.reviews);
  const isFetching = useAppSelector((state) => state.reviews.status === "loading");
  const error = useAppSelector((state) => state.reviews.error);

  // Local state
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedReview, setSelectedReview] = useState<ReviewDetails | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);

  /**
   * Fetch reviews on mount or when merchant ID changes
   */
  useEffect(() => {
    if (merchant?.userId) {
      dispatch(fetchReviews(merchant?.userId));
    }
  }, [merchant?.userId, dispatch]);

  /**
   * Callback to refresh reviews data
   * Invalidates cache and refetches
   */
  const handleRefetch = useCallback(() => {
    if (merchant?.userId) {
      dispatch(invalidateReviewCache());
      dispatch(fetchReviews(merchant.userId));
    }
  }, [merchant?.userId]);


  /**
   * Filter out dev reviews and apply search filter
   * Memoized to avoid unnecessary recalculations
   */
  const filteredReviews = useMemo(() => {
    const base = reviews
      .filter((r) => r.isDev !== "yes")
      ?.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    if (!searchTerm) return base;
    const s = searchTerm.toLowerCase();
    return base.filter(
      (r) =>
        (r.userName || "").toLowerCase().includes(s) ||
        (r.tripTitle || "").toLowerCase().includes(s)
    );
  }, [reviews, searchTerm]);

  /**
   * Apply pagination to filtered reviews
   * Page starts at 1 to match merchant screen pattern
   */
  const paginatedReviews = useMemo(() => {
    const start = (page - 1) * MAX_ROWS_PER_PAGE;
    return filteredReviews.slice(start, start + MAX_ROWS_PER_PAGE);
  }, [filteredReviews, page]);

  /**
   * Reset page when filtered set changes (e.g., on search)
   */
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);


  /**
   * Handle row click to open review details modal
   */
  const handleRowClick = (row: ReviewDetails) => {
    setSelectedReview(row);
    setOpenDialog(true);
  };

  if (isFetching) {
    return (
      <Grid container spacing={2} sx={{ p: 3 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Grid item xs={12} md={6} lg={4} key={i}>
            <Box
              sx={{
                height: 120,
                borderRadius: 2,
                bgcolor: "#F3F4F6",
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Failed to load reviews. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
        spacing={2}
        justifyContent={{ xs: "center" }}
      >
        <TextField
          id="review-search"
          variant="outlined"
          placeholder={
            isMobile ? "Search reviews..." : "Search by username, trip title"
          }
          value={searchTerm}
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", sm: "50%" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "50px",
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              transition: "box-shadow 0.2s ease-in-out",
              "&:hover": {
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.15)",
              },
              "&.Mui-focused": {
                boxShadow: `0px 2px 6px ${alpha(
                  theme.palette.primary.main,
                  0.25
                )}`,
              },
              "& fieldset": {
                borderColor: "#E5E7EB",
                borderRadius: "50px",
                transition: "border-color 0.2s ease-in-out",
              },
              "&:hover fieldset": {
                borderColor: "#D1D5DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputBase-input": {
              padding: "12px 14px",
            },
          }}
          slotProps={{
            input: {
              "aria-label": "Search reviews",
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: "#6B7280", mr: 1 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment
                  onClick={() => {
                    setSearchTerm("");
                    document.getElementById("review-search")?.focus();
                  }}
                  position="end"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    mr: 1,
                  }}
                >
                  <Close sx={{ color: "#6B7280", fontSize: "18px" }} />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Tooltip title="Refresh reviews" arrow TransitionComponent={Fade}>
            <Button
              variant="outlined"
              onClick={handleRefetch}
              sx={{
                borderRadius: "8px",
                py: 0.75,
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#D1D5DB",
                color: "#4B5563",
                "&:hover": {
                  borderColor: "#9CA3AF",
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Refresh
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: "16px",
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <Box
          p={{ xs: 1.5, sm: 2, md: 2.5 }}
          borderBottom="1px solid #E5E7EB"
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={2}
        >
          <Typography variant="h6" fontWeight="600" sx={{ color: "#1F2937" }}>
            Reviews{" "}
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1.5,
                py: 0.5,
                border: "1px solid #E5E7EB",
                borderRadius: "16px",
                bgcolor: "#F3F4F6",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                component="span"
                color="#4B5563"
                fontSize={14}
                fontWeight="500"
              >
                {filteredReviews.length} items
              </Typography>
            </Box>
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: { xs: "60vh", md: "70vh" } }}>
          <Table stickyHeader sx={{ minWidth: 700 }} aria-label="reviews table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  sx={{
                    color: "#6B7280",
                    fontWeight: "600",
                    fontSize: "14px",
                    bgcolor: "#F9FAFB",
                    borderBottom: "2px solid #E5E7EB",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textTransform: "uppercase",
                  }}
                >
                  User
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    color: "#6B7280",
                    fontWeight: "600",
                    fontSize: "14px",
                    bgcolor: "#F9FAFB",
                    borderBottom: "2px solid #E5E7EB",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textTransform: "uppercase",
                  }}
                >
                  Trip Title
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#6B7280", bgcolor: "#F9FAFB", fontWeight: 600 }}
                >
                  Review Given
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#6B7280", bgcolor: "#F9FAFB", fontWeight: 600 }}
                >
                  Host
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#6B7280", bgcolor: "#F9FAFB", fontWeight: 600 }}
                >
                  Merchant
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ color: "#6B7280", bgcolor: "#F9FAFB", fontWeight: 600 }}
                >
                  Trip
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ color: "#6B7280", bgcolor: "#F9FAFB", fontWeight: 600 }}
                >
                  Created At
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredReviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="#6B7280" variant="body1">
                      No reviews found
                      {searchTerm ? " matching your search" : ""}.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReviews.map((review, idx) => (
                  <TableRow
                    key={review.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                      backgroundColor: idx % 2 === 0 ? "white" : "#F9FAFB",
                    }}
                    onClick={() => handleRowClick(review)}
                  >
                    <TableCell
                      sx={{ color: "#374151", fontWeight: 500, py: 2 }}
                    >
                      <Typography sx={{ fontSize: "14px" }}>
                        {review?.userName}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{ color: "#374151", fontWeight: 500, py: 2 }}
                    >
                      <Typography sx={{ fontSize: "14px" }}>
                        {review?.tripTitle}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#374151",
                        fontWeight: 500,
                        py: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "14px" }}>
                        {["host", "merchant", "trip"].some((key) =>
                          Boolean(
                            review?.reviews?.[
                              key as keyof typeof review.reviews
                            ]
                              ?.toString()
                              .trim()
                          )
                        )
                          ? "Yes"
                          : "No"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Rating
                        value={review.rating.host}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Rating
                        value={review.rating.merchant}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Rating
                        value={review.rating.trip}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                    </TableCell>

                    <TableCell
                      sx={{ color: "#374151", fontWeight: 500, py: 2 }}
                    >
                      <Typography sx={{ fontSize: "14px" }}>
                        {format(review.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: "1px solid #E5E7EB" }}>
          <PaginationControl
            totalRows={filteredReviews?.length}
            page={page}
            onPageChange={(_e: unknown, p: number) => setPage(p)}
          />
        </Box>
      </Paper>

      {openDialog && (
        <ReviewModal
          open={openDialog}
          handleClose={() => setOpenDialog(false)}
          review={selectedReview as ReviewDetails}
          merchantName={merchant?.companyName || ""}
        />
      )}
    </Box>
  );
};

export default TripReviewsScreen;
